/* © NXAPP Limited */
import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

/**
 * Returns a helper that optimistically updates a cached list, then rolls back
 * if the mutation fails. Pass the same queryKey used in useQuery.
 */
export function useOptimisticList<T extends { id: string }>(queryKey: unknown[]) {
  const qc = useQueryClient()

  const optimisticUpdate = useCallback(
    async (updater: (old: T[]) => T[]) => {
      await qc.cancelQueries({ queryKey })
      const previous = qc.getQueryData<T[]>(queryKey)
      qc.setQueryData<T[]>(queryKey, old => updater(old ?? []))
      return previous
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc, JSON.stringify(queryKey)],
  )

  const rollback = useCallback(
    (previous: T[] | undefined) => {
      if (previous !== undefined) qc.setQueryData(queryKey, previous)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc, JSON.stringify(queryKey)],
  )

  return { optimisticUpdate, rollback }
}
